import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, B as debounce, e as emitEvent, l as logDeprecatedEventOnce, C as focusElement } from './utils.js';
import { d as defineCustomElement$2 } from './cl-tooltip2.js';
import { d as defineCustomElement$1 } from './cl-typography2.js';

class BackdropFactory {
  constructor() {
    /**
     * The backdrop element
     */
    this._backdropElement = document.createElement('div');
    /**
     * The HTML body element
     */
    this._bodyElement = document.body;
    /**
     * The HTML element
     */
    this._HTMLElement = document.documentElement;
    /**
     * The initial overflow value of the body element
     */
    this._initialBodyOverflow = '';
    /**
     * The initial overflow value of the html element
     */
    this._initialHTMLOverflow = '';
    /**
     * Whether the backdrop is currently visible or not
     */
    this._isActive = false;
    /**
     * Function that handles mounting/unmounting the backdrop element based on whether it
     * should be shown or hidden
     *
     * @param event The animation event
     */
    this.handleBackdropShowHide = (event) => {
      switch (event.animationName) {
        case 'show-backdrop':
          this._isActive = true;
          break;
        case 'hide-backdrop':
          this._contentElement.style.display = 'none';
          this._backdropElement.removeChild(this._contentElement);
          this._bodyElement.removeChild(this._backdropElement);
          if (this._initialBodyOverflow) {
            this._bodyElement.style.overflow = this._initialBodyOverflow;
          }
          else {
            this._bodyElement.style.removeProperty('overflow');
          }
          if (this._initialHTMLOverflow) {
            this._HTMLElement.style.overflow = this._initialHTMLOverflow;
          }
          else {
            this._HTMLElement.style.removeProperty('overflow');
          }
          this._isActive = false;
          break;
      }
    };
    this.calculateContentElementPosition = this.calculateContentElementPosition.bind(this);
    this._resizeObserver = new ResizeObserver(this.calculateContentElementPosition);
    this._backdropElement.classList.add('backdrop', 'backdrop--hidden');
    this._backdropElement.id = `id${Math.random().toString(16).slice(2)}`;
  }
  /**
   * Function that handles adding a resize observer to the parent element and the browser window
   */
  addResizeObservers() {
    this._resizeObserver.observe(this._parentElement);
    window.addEventListener('resize', this.calculateContentElementPosition);
  }
  /**
   * Function that handles removing the resize observer from the parent element and the browser window
   */
  disconnectResizeObservers() {
    this._resizeObserver.disconnect();
    window.removeEventListener('resize', this.calculateContentElementPosition);
  }
  /**
   * Function that updates the content element position based on the parent element position.
   */
  calculateContentElementPosition() {
    var _a, _b;
    const parentElement = this._parentElement.getBoundingClientRect();
    const bodyElement = this._bodyElement.getBoundingClientRect();
    const contentElementChild = this._contentElement.querySelector(':not(span)');
    if (contentElementChild) {
      if (![null, undefined, 'false'].includes(contentElementChild === null || contentElementChild === void 0 ? void 0 : contentElementChild.getAttribute('full-width'))) {
        const parentWidth = `${parentElement.width}px`;
        this._contentElement.style.setProperty('min-width', parentWidth);
        this._contentElement.style.setProperty('width', parentWidth);
        contentElementChild.style.setProperty('min-width', 'fit-content');
        contentElementChild.style.setProperty('width', parentWidth);
      }
    }
    const contentHeight = this._contentElement.clientHeight || ((_a = contentElementChild === null || contentElementChild === void 0 ? void 0 : contentElementChild.clientHeight) !== null && _a !== void 0 ? _a : 0) || 0;
    const contentWidth = this._contentElement.clientWidth || ((_b = contentElementChild === null || contentElementChild === void 0 ? void 0 : contentElementChild.clientWidth) !== null && _b !== void 0 ? _b : 0) || 0;
    switch (true) {
      case parentElement.bottom + contentHeight > bodyElement.bottom:
        this._contentElement.style.top = `${bodyElement.bottom - contentHeight}px`;
        break;
      case parentElement.bottom < bodyElement.top:
        this._contentElement.style.top = `${bodyElement.top}px`;
        break;
      default:
        this._contentElement.style.top = `${parentElement.top + parentElement.height}px`;
        break;
    }
    switch (true) {
      case parentElement.right > bodyElement.right:
        if (parentElement.right - parentElement.width + contentWidth < bodyElement.right) {
          this._contentElement.style.left = `${parentElement.left}px`;
        }
        else {
          this._contentElement.style.left = `${bodyElement.right - contentWidth}px`;
        }
        break;
      case parentElement.left < bodyElement.left:
        this._contentElement.style.left = `${bodyElement.left}px`;
        break;
      default:
        this._contentElement.style.left = `${parentElement.left}px`;
        break;
    }
  }
  /**
   * Function that handles initializing the backdrop eventListeners
   *
   * @param parentElement Element that is used to calculate the position of the content element
   * @param contentElement Element that is be displayed on top of the backdrop element-
   */
  init(parentElement, contentElement) {
    if (!parentElement) {
      throw new Error('Parent element must be provided');
    }
    if (!contentElement) {
      throw new Error('Content element must be provided');
    }
    contentElement.style.display = 'none';
    this._initialBodyOverflow = this._bodyElement.style.getPropertyValue('overflow');
    this._initialHTMLOverflow = this._HTMLElement.style.getPropertyValue('overflow');
    this._parentElement = parentElement;
    this._contentElement = contentElement;
    this._contentElement.style.position = 'absolute';
    this._contentElement.style.setProperty('z-index', 'calc(var(--z-index-10) + var(--z-index-9) + var(--z-index-1))');
    this._backdropElement.addEventListener('animationend', this.handleBackdropShowHide);
  }
  /**
   * Function that handles removing the backdrop eventListeners
   */
  disconnect() {
    this.hide();
    this._backdropElement.removeEventListener('animationend', this.handleBackdropShowHide);
  }
  /**
   * Function that handles hiding the backdrop
   */
  hide() {
    if (this._isActive) {
      this.disconnectResizeObservers();
      this._backdropElement.classList.remove('backdrop--active');
      this._backdropElement.classList.add('backdrop--hidden');
    }
  }
  /**
   * Function that handles showing the backdrop
   */
  show() {
    if (!this._isActive) {
      this.addResizeObservers();
      this._contentElement.style.display = 'block';
      this._backdropElement.appendChild(this._contentElement);
      this._bodyElement.style.overflow = 'hidden';
      this._HTMLElement.style.overflow = 'hidden';
      this._bodyElement.appendChild(this._backdropElement);
      this._backdropElement.classList.remove('backdrop--hidden');
      this._backdropElement.classList.add('backdrop--active');
      this.calculateContentElementPosition();
    }
  }
}

// Constants
const component = 'cl-field';
const fieldConstants = {
  VARIANT: {
    DEFAULT: 'default',
    SUBTLE: 'subtle',
  },
};
// Type-validators
const fieldValidator = {
  variant: createTypeValidator(fieldConstants.VARIANT),
};

const clFieldCss = ".sc-cl-field-h{--default-field-width:calc(4 * var(--size-10))}.field--default.sc-cl-field-h{--fixed-label-offset-left:var(--spacing-2);--fixed-label-offset-top:calc((var(--spacing-3) - var(--spacing-quarter)) * -1);--spacing-left-side:var(--spacing-2);--spacing-right-side:var(--spacing-2)}.field--subtle.sc-cl-field-h{--fixed-label-offset-left:0;--fixed-label-offset-top:calc(var(--spacing-2) * -1);--spacing-left-side:0;--spacing-right-side:var(--spacing-1)}.sc-cl-field-h{--border-color:rgb(var(--color-neutral-200));display:flex;flex:0 0 auto;flex-wrap:wrap;height:-moz-max-content;height:max-content;max-width:var(--field-width, var(--default-field-width));position:relative;width:var(--field-width, var(--default-field-width))}.sc-cl-field-h,.sc-cl-field-h *.sc-cl-field,.sc-cl-field-h *.sc-cl-field::before,.sc-cl-field-h *.sc-cl-field::after,.sc-cl-field-h .sc-cl-field-s>*,.sc-cl-field-h .sc-cl-field-s *,.sc-cl-field-h .sc-cl-field-s *::before,.sc-cl-field-h .sc-cl-field-s *::after{box-sizing:border-box}.sc-cl-field-h cl-tooltip.sc-cl-field{align-items:center;display:flex;max-width:100%;min-width:0;width:100%;z-index:var(--z-index-1)}.sc-cl-field-h .field.sc-cl-field{overflow:hidden}.sc-cl-field-h .field__content.sc-cl-field{background-color:var(--field-background-color, transparent);border:var(--border-width) solid transparent;cursor:pointer;display:flex;flex:1 1 100%;height:-moz-max-content;height:max-content;max-width:100%;min-height:calc(var(--size-5) + var(--size-half));min-width:0;outline:none;padding:0 var(--spacing-right-side) 0 var(--spacing-left-side);position:relative;z-index:var(--z-index-1)}.sc-cl-field-h .field__content--label.sc-cl-field{margin-top:var(--spacing-3)}.sc-cl-field-h .field__content.sc-cl-field:hover{--border-color:rgba(var(--color-neutral-200), var(--opacity-3))}.sc-cl-field-h .field__value.sc-cl-field{align-items:center;display:flex;min-width:0;padding:var(--spacing-1) 0;width:100%}.sc-cl-field-h .field__value.sc-cl-field::before{content:\"\";inset:0;position:absolute;width:100%}.sc-cl-field-h .field__value--placeholder.sc-cl-field{padding-left:var(--spacing-quarter);-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-field-h .field__value--value.sc-cl-field{min-width:0;width:100%;word-break:break-word}.sc-cl-field-h .field__label.sc-cl-field{align-items:center;color:rgb(var(--font-color-onlight-default));display:flex;position:absolute;-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-field-h .field__label--floating.sc-cl-field{height:calc(var(--size-1) * 5.5);left:var(--floating-label-offset-left);padding:0 var(--spacing-quarter);top:0;transition:all var(--animation-duration-fast)}.sc-cl-field-h .field__label.sc-cl-field:not(.field__label--floating){cursor:default;height:var(--size-2);left:var(--fixed-label-offset-left);top:var(--fixed-label-offset-top);transition:all var(--animation-duration-fast)}.sc-cl-field-h .field__helper-text.sc-cl-field{color:rgb(var(--font-color-onlight-default));display:flex;flex:1 1 100%;height:var(--size-2);justify-content:flex-start;margin-top:var(--spacing-half);padding:0 var(--spacing-left-side);-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-field-h:not(.field--disabled).field--default .field__content.sc-cl-field{border-color:var(--border-color);border-radius:var(--border-radius-interactive-small)}.sc-cl-field-h:not(.field--disabled).field--default .field__content.sc-cl-field:focus-visible{border-color:rgb(var(--system-color-focus))}.sc-cl-field-h:not(.field--disabled).field--subtle .field__content.sc-cl-field{border-bottom:var(--border-width) solid var(--border-color)}.sc-cl-field-h:not(.field--disabled).field--subtle .field__content.sc-cl-field:focus-visible{border-bottom:var(--border-width) solid rgb(var(--system-color-focus))}.field--full-width.sc-cl-field-h{--default-field-width:100%}.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=leading],.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=trailing]{filter:grayscale(1) contrast(0.5) opacity(0.5)}.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=leading]>*,.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=trailing]>*{position:relative}.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=leading]>*::after,.sc-cl-field-h.field--disabled .sc-cl-field-s>[slot=trailing]>*::after{content:\"\";height:100%;inset:0;opacity:0.5;position:absolute;width:100%}.field--disabled.field.sc-cl-field-h .field__label.sc-cl-field,.field--disabled.field.sc-cl-field-h .field__helper-text.sc-cl-field{cursor:not-allowed;--typography-color:rgba(var(--font-color-onlight-muted))}.field--disabled.field.sc-cl-field-h .field__content.sc-cl-field{cursor:not-allowed;--border-color:rgba(var(--color-neutral-100));color:rgba(var(--font-color-onlight-muted))}.field--disabled.field.sc-cl-field-h .field__value--value.sc-cl-field{cursor:not-allowed;--typography-color:rgba(var(--font-color-onlight-muted))}.field--disabled.field--default.sc-cl-field-h .field__content.sc-cl-field{background-color:rgba(var(--color-neutral-100));border-radius:var(--border-radius-interactive-small)}.field--disabled.field--subtle.sc-cl-field-h .field__content.sc-cl-field{border-bottom:var(--border-width) solid var(--border-color)}.field--focused.sc-cl-field-h:not(.field--disabled) .field__label.sc-cl-field{--typography-color:rgba(var(--font-color-onlight-brand))}.field--focused.sc-cl-field-h:not(.field--disabled) .field__content.sc-cl-field{--border-color:rgba(var(--color-brand-main))}.field--focused.sc-cl-field-h:not(.field--disabled) .field__content.sc-cl-field:hover{--border-color:rgba(var(--color-brand-main))}.sc-cl-field-h .field__slot--leading.sc-cl-field,.sc-cl-field-h .field__slot--trailing.sc-cl-field{align-items:center;display:flex;height:calc(var(--size-5) + var(--size-half));justify-content:center;max-height:calc(var(--size-5) + var(--size-half));position:relative;z-index:var(--z-index-1)}.sc-cl-field-h .field__slot--content.sc-cl-field{display:flex;flex:1 0 100%;width:100%}.sc-cl-field-h .sc-cl-field-s>[slot=leading],.sc-cl-field-h .sc-cl-field-s>[slot=trailing]{max-height:var(--size-3);max-width:var(--size-4)}.sc-cl-field-h .sc-cl-field-s>div[slot=leading],.sc-cl-field-h .sc-cl-field-s>span[slot=leading],.sc-cl-field-h .sc-cl-field-s>div[slot=trailing],.sc-cl-field-h .sc-cl-field-s>span[slot=trailing]{align-items:center;display:flex;justify-content:center;position:relative}.sc-cl-field-h .sc-cl-field-s>[slot=leading]{color:rgba(var(--color-brand-300));padding-right:var(--spacing-half)}.sc-cl-field-h .sc-cl-field-s>[slot=leading]:last-child{padding-right:var(--spacing-1)}.sc-cl-field-h .sc-cl-field-s>[slot=trailing]{padding-left:var(--spacing-half)}.sc-cl-field-h .sc-cl-field-s>[slot=trailing]:first-child{padding-left:var(--spacing-1)}";

const ClField = /*@__PURE__*/ proxyCustomElement(class ClField extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clBlur = createEvent(this, "clBlur", 7);
    this.clFocus = createEvent(this, "clFocus", 7);
    this.clHideContent = createEvent(this, "clHideContent", 7);
    this.clShowContent = createEvent(this, "clShowContent", 7);
    this.blur = createEvent(this, "blur", 7);
    this.focus = createEvent(this, "focus", 7);
    this.hideContent = createEvent(this, "hideContent", 7);
    this.showContent = createEvent(this, "showContent", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * The backdrop class
     */
    this.backdrop = new BackdropFactory();
    /**
     * The last pressed keyboard key
     */
    this.lastPressedKey = '';
    /**
     * Object containing the references to a few DOM elements
     */
    this.references = {
      field: undefined,
      helperText: undefined,
      label: undefined,
      value: undefined,
    };
    /**
     * Whether the floating label can be shown or not
     */
    this.showFloatingLabel = false;
    /**
     * Function that renders the placeholder, or renders the value inside a tooltip (or not) based on
     * whether the `showDetails` attribute is set to true or not
     */
    this.renderValueElement = () => {
      var _a;
      const valueIsSet = (_a = this.value) === null || _a === void 0 ? void 0 : _a.length;
      const showPlaceholder = this.placeholder && !valueIsSet && this.placeholder.length;
      const valueElement = (h("div", { ref: el => {
          this.references.value = el;
        }, class: 'field__value' }, showPlaceholder ? (h("cl-typography", { class: 'field__value--placeholder', type: 'disabled', "no-wrap": true }, this.placeholder)) : (h("cl-typography", { class: 'field__value--value', "no-wrap": !this.multiline }, this.value))));
      return this.showDetails && valueIsSet ? (h("cl-tooltip", { label: this.value, position: 'top', arrow: true, onClInit: this.updateTooltipLabelStyles }, valueElement)) : (valueElement);
    };
    /**
     * Function that clears the lastPressedKey-state after 125ms
     */
    this.resetLastPressedKey = debounce(() => (this.lastPressedKey = ''), 125);
    this.isActive = false;
    this.isFocused = false;
    this.disabled = false;
    this.floatingLabel = false;
    this.fullWidth = false;
    this.helperText = '';
    this.label = '';
    this.multiline = false;
    this.placeholder = '';
    this.showDetails = false;
    this.value = '';
    this.variant = 'default';
  }
  /**
   * Updates the states based on whether the field is disabled or not
   */
  handleDisabled(disabled) {
    if (disabled) {
      this.isFocused = false;
      this.isActive = false;
      this.backdrop.hide();
      emitEvent(this, 'clHideContent', { component }, null);
      emitEvent(this, 'clBlur', { component }, null);
    }
  }
  /**
   * Validates whether the floating-label attribute is enabled, or not, while showing a placeholder
   */
  validateFloatingLabelPlaceholder() {
    if (this.placeholder && this.floatingLabel && this.placeholder.length) {
      this.logger.error.attribute.floatingLabel.placeholder('A placeholder and floating label cannot be used simultaneously.');
    }
  }
  /**
   * Validates whether the given variant attribute is valid or not
   */
  validateVariant(variant) {
    if (!fieldValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
    }
  }
  /**
   * Handles setting showFloatingLabel to true/false based on the current state
   */
  updateShowFloatingLabel() {
    var _a;
    this.showFloatingLabel = !!(this.floatingLabel && !this.isFocused && !this.isActive && !((_a = this.value) === null || _a === void 0 ? void 0 : _a.length));
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the floating-label attribute and the placeholder attribute;
   * - Set showFloatingLabel to true/false;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'blur', 'clBlur');
    logDeprecatedEventOnce(this.logger, 'focus', 'clFocus');
    logDeprecatedEventOnce(this.logger, 'hideContent', 'clHideContent');
    logDeprecatedEventOnce(this.logger, 'showContent', 'clShowContent');
    this.validateFloatingLabelPlaceholder();
    this.updateShowFloatingLabel();
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Disconnect the backdrop and its eventlisteners;
   */
  disconnectedCallback() {
    if (this.slottedElement) {
      this.backdrop.hide();
      this.backdrop.disconnect();
    }
  }
  /**
   * Just once before the component has fully loaded and rendered:
   * - Get and set the slotted element;
   * - Initialize the backdrop (if a slotted element is present);
   */
  componentWillLoad() {
    const slottedElement = this.element.querySelector('span[slot="content"]');
    if (slottedElement) {
      this.updateSlottedElementOffset();
      this.backdrop.init(this.element, slottedElement);
      this.slottedElement = slottedElement;
    }
  }
  /**
   * Before every render:
   * - Update the top-margin of the slotted element to overlap the helper-text;
   */
  componentWillRender() {
    this.updateSlottedElementOffset();
  }
  /**
   * After every render:
   * - Recalculate the floating-label position (if it is enabled);
   */
  componentDidRender() {
    if (this.showFloatingLabel) {
      this.updateFloatingLabelOffset();
    }
  }
  /**
   * Handles changing the isFocused/isActive state based on the user's pointer/mouse input
   */
  handleWindowClick(event) {
    var _a;
    const clickedOnFixedLabel = event.target === this.references.label &&
      !event.target.className.includes('field__label--floating');
    const clickedOnField = this.references.field && this.references.field.contains(event.target) && !clickedOnFixedLabel;
    const clickedOnSlotted = (_a = this.slottedElement) === null || _a === void 0 ? void 0 : _a.contains(event.target);
    // If a slotted element is set
    if (this.slottedElement) {
      // and the field is not focused
      if (!this.isFocused) {
        // and the user clicks on the field element
        if (clickedOnField && !this.disabled) {
          this.isFocused = true;
          this.isActive = true;
          this.backdrop.show();
          emitEvent(this, 'clFocus', { component, originalEvent: event }, null);
          emitEvent(this, 'clShowContent', { component }, null);
        }
      }
      else {
        // and the field is focused already
        // and the field is not active
        if (!this.isActive && !this.disabled) {
          // and the user clicks on the field element
          if (clickedOnField) {
            this.isFocused = true;
            this.isActive = true;
            this.backdrop.show();
            emitEvent(this, 'clShowContent', { component }, null);
          }
          else {
            // and the user clicks somewhere outside the field element
            this.isFocused = false;
            this.isActive = false;
            this.backdrop.hide();
            emitEvent(this, 'clBlur', { component, originalEvent: event }, null);
          }
        }
        else {
          // and the field is active
          // and the user clicks somewhere outside the slotted element
          if (!clickedOnSlotted) {
            this.isFocused = true;
            this.isActive = false;
            this.backdrop.hide();
            emitEvent(this, 'clHideContent', { component }, null);
          }
        }
      }
    }
    // If no slotted element is set
    if (!this.slottedElement) {
      // and the field is not focused
      if (!this.isFocused) {
        // and the user clicks on the field element
        if (clickedOnField) {
          this.isFocused = true;
          this.isActive = true;
          emitEvent(this, 'clFocus', { component, originalEvent: event }, null);
        }
      }
      else {
        // and the field is focused already
        // and the user clicks on the field or anywhere else
        this.isFocused = false;
        this.isActive = false;
        this.backdrop.hide();
        emitEvent(this, 'clBlur', { component, originalEvent: event }, null);
      }
    }
  }
  /**
   * Handles changing the isFocused/isActive state based on the user's keyboard input while focused on the field
   */
  handleWindowKeydown(event) {
    const pressedKey = event.code;
    // If the field is focused
    if (this.isFocused) {
      // and a slotted element is set
      if (this.slottedElement) {
        // and the field is not active
        if (!this.isActive) {
          switch (pressedKey) {
            case 'Tab':
              {
                this.isFocused = false;
                this.isActive = false;
                emitEvent(this, 'clBlur', { component, originalEvent: event }, null);
              }
              break;
            case 'Enter':
            case 'Space':
              {
                event.preventDefault();
                this.isFocused = true;
                this.isActive = true;
                this.backdrop.show();
                emitEvent(this, 'clFocus', { component, originalEvent: event }, null);
                emitEvent(this, 'clShowContent', { component }, null);
              }
              break;
          }
        }
        else {
          // and the field is active
          switch (pressedKey) {
            case 'Tab':
              event.preventDefault();
            case 'Escape':
              {
                this.isFocused = true;
                this.isActive = false;
                this.backdrop.hide();
                emitEvent(this, 'clHideContent', { component }, null);
                if (this.references.field) {
                  focusElement(this.references.field);
                }
              }
              break;
          }
        }
      }
      // and no slotted element is set
      if (!this.slottedElement) {
        if (['Tab', 'Escape'].includes(pressedKey)) {
          this.isFocused = false;
          this.isActive = false;
          emitEvent(this, 'clBlur', { component, originalEvent: event }, null);
        }
      }
    }
    this.lastPressedKey = pressedKey;
    this.resetLastPressedKey();
  }
  /**
   * Handles setting the isFocused state to true when the field is focused on by tabbing
   */
  handleFocus(event) {
    if (this.lastPressedKey === 'Tab' && event.target === this.references.field && !this.disabled) {
      this.isFocused = true;
      emitEvent(this, 'clFocus', { component, originalEvent: event }, null);
    }
  }
  /**
   * Function that calculates the offset-left of the floating-label element, in order to always
   * position it correctly in the field
   */
  updateFloatingLabelOffset() {
    var _a, _b;
    this.element.style.setProperty('--floating-label-offset-left', `${(_b = (_a = this.references.value) === null || _a === void 0 ? void 0 : _a.offsetLeft) !== null && _b !== void 0 ? _b : 0}px`);
  }
  /**
   * Function that calculates the margin-top of the slotted element, in order to always
   * overlap the helper-text
   */
  updateSlottedElementOffset() {
    var _a;
    if (this.slottedElement && this.references.helperText) {
      this.slottedElement.style.setProperty('margin-top', `-${(_a = this.references.helperText.offsetHeight) !== null && _a !== void 0 ? _a : 16}px`);
    }
  }
  /**
   * Function that updates the tooltip element label styling, in order to show the field value nicely
   */
  updateTooltipLabelStyles(event) {
    var _a;
    const tooltipLabelElement = (_a = event.target) === null || _a === void 0 ? void 0 : _a.querySelector('.tooltip__label');
    if (tooltipLabelElement) {
      tooltipLabelElement.style.height = 'auto';
      tooltipLabelElement.style.width = 'auto';
      tooltipLabelElement.style.margin = 'var(--spacing-1) var(--spacing-2)';
    }
  }
  render() {
    const { disabled, fullWidth, helperText, label, variant } = this;
    return (h(Host, { class: cl('field', `field--${variant}`, {
        'field--focused': this.isFocused,
        'field--full-width': fullWidth,
        'field--disabled': disabled,
      }), tabIndex: -1 }, h("div", { ref: el => {
        this.references.field = el;
      }, class: cl('field__content', { ['field__content--label']: label }), tabIndex: 0 }, h("div", { class: 'field__slot--leading' }, h("slot", { name: 'leading' })), h("cl-typography", { ref: el => {
        this.references.label = el;
      }, class: cl('field__label', {
        'field__label--floating': this.showFloatingLabel,
      }), tabIndex: -1, variant: this.showFloatingLabel ? 'body-1' : 'body-2' }, label), this.renderValueElement(), h("div", { class: 'field__slot--trailing' }, h("slot", { name: 'trailing' }))), helperText && (h("cl-typography", { ref: el => {
        this.references.helperText = el;
      }, class: 'field__helper-text', tabIndex: -1, variant: 'microcopy' }, helperText)), h("div", { class: 'field__slot--content' }, h("slot", { name: 'content' }))));
  }
  get element() { return this; }
  static get watchers() { return {
    "disabled": ["handleDisabled"],
    "floatingLabel": ["validateFloatingLabelPlaceholder", "updateShowFloatingLabel"],
    "placeholder": ["validateFloatingLabelPlaceholder"],
    "variant": ["validateVariant"],
    "isFocused": ["updateShowFloatingLabel"],
    "isActive": ["updateShowFloatingLabel"],
    "value": ["updateShowFloatingLabel"]
  }; }
  static get style() { return clFieldCss; }
}, [6, "cl-field", {
    "disabled": [4],
    "floatingLabel": [4, "floating-label"],
    "fullWidth": [4, "full-width"],
    "helperText": [1, "helper-text"],
    "label": [1],
    "multiline": [4],
    "placeholder": [1],
    "showDetails": [4, "show-details"],
    "value": [1],
    "variant": [1],
    "isActive": [32],
    "isFocused": [32]
  }, [[16, "click", "handleWindowClick"], [8, "keydown", "handleWindowKeydown"], [2, "focus", "handleFocus"]]]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-field", "cl-tooltip", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-field":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClField);
      }
      break;
    case "cl-tooltip":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClField as C, defineCustomElement as d };

//# sourceMappingURL=cl-field2.js.map